import { router, usePage } from '@inertiajs/react'
import AccountLayout from '@/layouts/AccountLayout'
import type { PlanState, PricingProps, SharedProps } from '@/types'
import { useAccountPath } from '@/lib/useAccountPath'

interface BillingProps {
  plan: PlanState
  pricing: PricingProps
  currency_locked: boolean
}

const STATUS_LABEL: Record<string, string> = {
  trialing: 'Free trial',
  active: 'Active',
  past_due: 'Payment failed, retrying',
  canceled: 'Canceled',
  expired: 'Expired'
}

const STATUS_BADGE: Record<string, string> = {
  trialing: 'badge-info',
  active: 'badge-success',
  past_due: 'badge-error',
  canceled: 'badge-ghost',
  expired: 'badge-warning'
}

const CURRENCY_LABEL: Record<string, string> = {
  GBP: 'GBP (£)',
  USD: 'USD ($)',
  EUR: 'EUR (€)'
}

export default function Billing({ plan, pricing, currency_locked }: BillingProps) {
  const { current_membership } = usePage<{ props: SharedProps }>().props as unknown as SharedProps
  const ap = useAccountPath()
  const isOwner = current_membership?.owner ?? false

  const price = pricing.prices[pricing.currency]

  function subscribe(interval: 'monthly' | 'annual') {
    router.post(ap('/billing/checkout'), { interval })
  }

  function openPortal() {
    router.post(ap('/billing/portal'))
  }

  function changeCurrency(currency: string) {
    router.patch(ap('/billing/currency'), { currency }, { preserveScroll: true })
  }

  return (
    <AccountLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Billing</h1>
        </div>

        <div className="card bg-base-100">
          <div className="card-body">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="card-title text-lg">Current plan</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xl font-semibold capitalize">{plan.plan === 'pro' ? 'Pro' : plan.plan === 'trialing' ? 'Free Trial' : 'Inactive'}</span>
                  <span className={`badge ${STATUS_BADGE[plan.status]} badge-sm`}>
                    {STATUS_LABEL[plan.status]}
                  </span>
                </div>

                {plan.status === 'trialing' && plan.trial_days_remaining !== null && (
                  <p className="text-sm text-base-content/60 mt-2">
                    {plan.trial_days_remaining === 0
                      ? "Your trial ends today."
                      : `${plan.trial_days_remaining} ${plan.trial_days_remaining === 1 ? 'day' : 'days'} remaining in your trial.`}
                  </p>
                )}

                {plan.status === 'active' && !plan.grace_period_ends_at && (
                  <p className="text-sm text-base-content/60 mt-2">
                    Billed in {pricing.currency}.
                  </p>
                )}

                {plan.grace_period_ends_at && (
                  <p className="text-sm text-base-content/60 mt-2">
                    Your subscription is canceled. Access continues until{' '}
                    {new Date(plan.grace_period_ends_at).toLocaleDateString()}.
                  </p>
                )}
              </div>

              {plan.status === 'active' || plan.status === 'past_due' || plan.status === 'canceled' ? (
                isOwner && (
                  <button onClick={openPortal} className="btn btn-primary btn-sm">
                    Manage billing
                  </button>
                )
              ) : null}
            </div>
          </div>
        </div>

        {(plan.status === 'trialing' || plan.status === 'expired') && isOwner && (
          <div className="card bg-base-100 border-2 border-primary">
            <div className="card-body">
              <h2 className="card-title text-lg">
                {plan.status === 'trialing' ? 'Subscribe before your trial ends' : 'Subscribe to continue'}
              </h2>
              <p className="text-sm text-base-content/60">
                Pick a billing interval. You can change it at any time.
              </p>

              {!currency_locked && (
                <fieldset className="fieldset mt-2">
                  <label className="label" htmlFor="billing-currency">Currency</label>
                  <select
                    id="billing-currency"
                    className="select select-sm w-full sm:w-56"
                    value={pricing.currency}
                    onChange={(e) => changeCurrency(e.target.value)}
                  >
                    {(['GBP', 'USD', 'EUR'] as const).map((code) => (
                      <option key={code} value={code}>{CURRENCY_LABEL[code]}</option>
                    ))}
                  </select>
                  <p className="label whitespace-normal">
                    Set from your location when you signed up. You can change it until you subscribe.
                  </p>
                </fieldset>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <button
                  onClick={() => subscribe('monthly')}
                  className="card bg-base-200 hover:bg-base-300 transition cursor-pointer text-start"
                >
                  <div className="card-body">
                    <h3 className="font-semibold">Monthly</h3>
                    <div className="text-2xl font-bold mt-1">
                      {price.symbol}{price.monthly}
                      <span className="text-sm text-base-content/60 font-normal"> / month</span>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => subscribe('annual')}
                  className="card bg-primary/5 hover:bg-primary/10 transition cursor-pointer text-start border border-primary"
                >
                  <div className="card-body">
                    <h3 className="font-semibold">Annual <span className="badge badge-primary badge-sm ms-1">Save ~15%</span></h3>
                    <div className="text-2xl font-bold mt-1">
                      {price.symbol}{price.annual}
                      <span className="text-sm text-base-content/60 font-normal"> / year</span>
                    </div>
                  </div>
                </button>
              </div>

              <p className="text-xs text-base-content/40 mt-4">
                {currency_locked && 'Your billing currency is fixed by your subscription. '}
                All prices exclusive of VAT/sales tax, which is charged at your local rate whichever currency you pick.
              </p>
            </div>
          </div>
        )}

        {!isOwner && (
          <div role="alert" className="alert alert-soft alert-info text-sm">
            Only organization owners can manage billing. Ask the owner to subscribe.
          </div>
        )}
      </div>
    </AccountLayout>
  )
}
